import React, {useState, memo, useRef} from "react";
import { Template } from "keycloakify/lib/components/Template";
import type { KcProps } from "keycloakify";
import {getMsg, KcContextBase} from "keycloakify";
import { useCssAndCx } from "tss-react";
import { useConstCallback } from "powerhooks/useConstCallback";
import type { FormEventHandler } from "react";

export const Login = memo(({ kcContext, ...props }: { kcContext: KcContextBase.Login } & KcProps) => {
    const { social, realm, url, usernameEditDisabled, login, auth, registrationDisabled } = kcContext;

    const socialLinkRef = useRef<HTMLAnchorElement>(null);

    const { msg, msgStr } = getMsg(kcContext);

    const { cx } = useCssAndCx();

    const [isLoginButtonDisabled, setIsLoginButtonDisabled] = useState(false);

    const newURL = new URL(url.loginAction);
    let clientId = newURL.searchParams.get("client_id");

    let referralLink = '';
    let jotformLink = '';
    let registrationLink = '';
    let ssoDomain = '';

    const [socialEmail, setSocialEmail] = useState('');
    const [socialEmailTouched, setSocialEmailTouched] = useState(false);
    const [socialEmailLoginClicked, setSocialEmailLoginClicked] = useState(false);
    const isValidSocialEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(socialEmail);
    const showSocialEmailHint = !isValidSocialEmail && ((socialEmailTouched && socialEmail.length > 0) || socialEmailLoginClicked);

    const handleSocialEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSocialEmail(event.target.value);
    };

    const handleSocialEmailKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            socialLinkRef.current?.click();
        }
    };

    switch(clientId) {
        case 'mentalhealth': {
            referralLink = 'https://bh.aspennetworkinc.org/public/referral';
            jotformLink = 'https://bh.aspennetworkinc.org/public/waiver';
            registrationLink = 'https://bh.aspennetworkinc.org/public/registration';
            ssoDomain = 'yourname@jeffco.edu'
            break;
        }
        case 'mentalhealth-test': {
            referralLink = 'https://jchn-dashboard-test.azurewebsites.net/public/referral';
            jotformLink = 'https://jchn-dashboard-test.azurewebsites.net/public/waiver';
            registrationLink = 'https://jchn-dashboard-test.azurewebsites.net/public/registration';
            ssoDomain = 'yourname@jeffcotest.org'
            break;
        }
        case 'mentalhealth-demo': {
            referralLink = 'https://jchn-dashboard-demo.azurewebsites.net/public/referral';
            jotformLink = 'https://jchn-dashboard-demo.azurewebsites.net/public/waiver';
            registrationLink = 'https://jchn-dashboard-demo.azurewebsites.net/public/registration';
            ssoDomain = 'yourname@jeffcotest.org'
            break;
        }
        case 'mentalhealth-local': {
            referralLink = 'http://localhost:4200/public/referral';
            jotformLink = 'http://localhost:4200/waiver';
            registrationLink = 'http://localhost:4200/registration'
            ssoDomain = 'yourname@jeffcotest.org'
            break;
        }
        default: {
            // Test
            referralLink = 'https://jchn-dashboard-test.azurewebsites.net/public/referral';
            jotformLink = 'https://hipaa.jotform.com/222895470464060';
            ssoDomain = 'yourname@jeffco.edu'
        }
    }

    const onSubmit = useConstCallback<FormEventHandler<HTMLFormElement>>(e => {
        e.preventDefault();

        setIsLoginButtonDisabled(true);

        const formElement = e.target as HTMLFormElement;

        //NOTE: Even if we login with email Keycloak expect username and password in
        //the POST request.
        formElement.querySelector("input[name='email']")?.setAttribute("name", "username");

        formElement.submit();
    });

    return (
        <Template
            {...{ kcContext, ...props }}
            doFetchDefaultThemeResources={true}
            displayInfo={social.displayInfo}
            displayWide={realm.password && social.providers !== undefined}
            headerNode={msg("doLogIn")}
            formNode={
                <div id="kc-form" className={cx(realm.password && social.providers !== undefined && props.kcContentWrapperClass)}>
                    <div
                        id="kc-form-wrapper"
                        className={cx(realm.password && social.providers && [props.kcFormSocialAccountContentClass, props.kcFormSocialAccountClass])}
                    >
                        {realm.password && (
                            <form id="kc-form-login" onSubmit={onSubmit} action={url.loginAction} method="post">
                                <div className={cx(props.kcFormGroupClass)}>
                                    {(() => {
                                        const label = !realm.loginWithEmailAllowed
                                            ? "username"
                                            : realm.registrationEmailAsUsername
                                            ? "email"
                                            : "usernameOrEmail";

                                        const autoCompleteHelper: typeof label = label === "usernameOrEmail" ? "username" : label;

                                        return (
                                            <>
                                                <label htmlFor={autoCompleteHelper} className={cx(props.kcLabelClass)}>
                                                    ASPEN Username or Email
                                                </label>
                                                <input
                                                    tabIndex={1}
                                                    id={autoCompleteHelper}
                                                    className={cx(props.kcInputClass)}
                                                    //NOTE: This is used by Google Chrome auto fill so we use it to tell
                                                    //the browser how to pre fill the form but before submit we put it back
                                                    //to username because it is what keycloak expects.
                                                    name={autoCompleteHelper}
                                                    defaultValue={login.username ?? ""}
                                                    type="text"
                                                    {...(usernameEditDisabled
                                                        ? { "disabled": true }
                                                        : {
                                                              "autoFocus": true,
                                                              "autoComplete": "off",
                                                          })}
                                                />
                                            </>
                                        );
                                    })()}
                                </div>
                                <div className={cx(props.kcFormGroupClass)}>
                                    <label htmlFor="password" className={cx(props.kcLabelClass)}>
                                        {msg("password")}
                                    </label>
                                    <input
                                        tabIndex={2}
                                        id="password"
                                        className={cx(props.kcInputClass)}
                                        name="password"
                                        type="password"
                                        autoComplete="off"
                                    />
                                </div>
                                <div className={cx(props.kcFormGroupClass, props.kcFormSettingClass)}>
                                    <div id="kc-form-options">
                                        {realm.rememberMe && !usernameEditDisabled && (
                                            <div className="checkbox">
                                                <label>
                                                    <input
                                                        tabIndex={3}
                                                        id="rememberMe"
                                                        name="rememberMe"
                                                        type="checkbox"
                                                        {...(login.rememberMe
                                                            ? {
                                                                  "checked": true,
                                                              }
                                                            : {})}
                                                    />
                                                    {msg("rememberMe")}
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                    <div className={cx(props.kcFormOptionsWrapperClass)}>
                                        {realm.resetPasswordAllowed && (
                                            <span>
                                                <a className={'link'} tabIndex={5} href={url.loginResetCredentialsUrl}>
                                                    {msg("doForgotPassword")}
                                                </a>
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div id="kc-form-buttons" className={cx(props.kcFormGroupClass)}>
                                    <input
                                        type="hidden"
                                        id="id-hidden-input"
                                        name="credentialId"
                                        {...(auth?.selectedCredential !== undefined
                                            ? {
                                                  "value": auth.selectedCredential,
                                              }
                                            : {})}
                                    />
                                    <input
                                        tabIndex={4}
                                        className={cx(
                                            props.kcButtonPrimaryClass,
                                            props.kcButtonBlockClass,
                                            props.kcButtonLargeClass,
                                            "login-button",
                                        )}
                                        name="login"
                                        id="kc-login"
                                        type="submit"
                                        value={msgStr("doLogIn")}
                                        disabled={isLoginButtonDisabled}
                                    />
                                </div>
                            </form>
                        )}
                        <br/>
                        <a href={referralLink}>
                            <button className={cx(
                                props.kcButtonPrimaryClass,
                                props.kcButtonBlockClass,
                                props.kcButtonLargeClass,
                            )}>Student Public Referral</button>
                        </a>
                        <a href={jotformLink}>
                            <button className={cx(
                                props.kcButtonPrimaryClass,
                                props.kcButtonBlockClass,
                                props.kcButtonLargeClass,
                            )}>Complete Parent Waiver</button>
                        </a>
                    </div>
                    {realm.password && social.providers !== undefined && (

                        <div id="kc-social-providers" className={cx(props.kcFormSocialAccountContentClass, props.kcFormSocialAccountClass)}>

                            <div className="social-email-container">
                                <label className={cx(props.kcLabelClass)}>
                                    Jefferson College Student Account
                                </label>
                                <input
                                    onChange={handleSocialEmailChange}
                                    onKeyDown={handleSocialEmailKeyDown}
                                    onBlur={() => {if (socialEmail?.length > 0) {setSocialEmailTouched(true);}}}
                                    value={socialEmail}
                                    placeholder={ssoDomain}
                                    tabIndex={3}
                                    id="socialLinkEmail"
                                    name="socialLinkEmail"
                                    className={cx(props.kcInputClass)}
                                    type="email"
                                />
                                {showSocialEmailHint && (
                                    <span className="social-email-hint">
                                        Enter a valid email address to sign in with your school account.
                                    </span>
                                )}
                            </div>

                            <ul
                                className={cx(
                                    props.kcFormSocialAccountListClass,
                                    social.providers.length > 4 && props.kcFormSocialAccountDoubleListClass,
                                )}
                            >
                                {social.providers.map(p => (
                                    <li key={p.providerId} className={cx(props.kcFormSocialAccountListLinkClass)}>
                                        <a
                                            ref={social.providers?.length === 1 ? socialLinkRef : undefined}
                                            href={isValidSocialEmail ? `${p.loginUrl}&login_hint=${encodeURIComponent(socialEmail)}` : undefined}
                                            id={`zocial-${p.alias}`}
                                            className={cx("zocial", p.providerId)}
                                            onClick={e => { if (!isValidSocialEmail) { e.preventDefault(); setSocialEmailLoginClicked(true); } }}
                                            title={!isValidSocialEmail ? "Please enter your email address to sign in with your school account" : undefined}
                                        >
                                            <span>Sign In with {p.displayName}</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            }
            infoNode={
                realm.password &&
                realm.registrationAllowed &&
                !registrationDisabled && (
                    <div id="kc-registration">
                        <span>
                            {msg("noAccount")}
                            <a className={'link'} tabIndex={6} href={registrationLink}>
                                {msg("doRegister")}
                            </a>
                        </span>
                    </div>
                )
            }
        />
    );
});
